import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "@src/lib/utils/cn";
import { TabStripCount } from "@src/lib/ui/tabStrip";

/**
 * NavList: a vertical list of sections or settings in a left pane (a dialog's
 * side column, a settings page's section list). Two semantics, one look:
 *
 * - Inside `NavListTabs` the rows switch a panel in place: a vertical
 *   tablist (Radix Tabs, orientation vertical; ↑/↓, Home / End, one tab
 *   stop). Pair each row with a `NavListPanel`.
 * - On its own it is a `<nav>` of links (`href`, or `asChild` around a Next
 *   Link) or buttons (`onClick`), and `current` sets aria-current="page".
 *
 * See `## Nav list` in design.md.
 */

type NavListTabsContextValue = {
  value: string | undefined;
  select: (value: string) => void;
  /** Values with a mounted NavListPanel. */
  panels: Readonly<Record<string, number>>;
  registerPanel: (value: string) => () => void;
};

const NavListTabsContext = React.createContext<NavListTabsContextValue | null>(
  null,
);

export type NavListTabsProps = Omit<
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
  "orientation" | "dir"
>;

/** Makes the NavList inside a vertical tablist whose rows switch panels. */
const NavListTabs = React.forwardRef<HTMLDivElement, NavListTabsProps>(
  function NavListTabs(
    { value: valueProp, defaultValue, onValueChange, ...props },
    ref,
  ) {
    // Controlled internally, with Radix's semantics (an undefined `value` is
    // uncontrolled), so a row can select itself on a bare click.
    const [innerValue, setInnerValue] = React.useState(defaultValue);
    const isControlled = valueProp !== undefined;
    const value = isControlled ? valueProp : innerValue;
    const select = React.useCallback(
      (next: string) => {
        if (!isControlled) setInnerValue(next);
        onValueChange?.(next);
      },
      [isControlled, onValueChange],
    );
    const [panels, setPanels] = React.useState<Record<string, number>>({});
    const registerPanel = React.useCallback((panelValue: string) => {
      const bump = (by: number) =>
        setPanels((prev) => {
          const next = { ...prev, [panelValue]: (prev[panelValue] ?? 0) + by };
          if (next[panelValue] <= 0) delete next[panelValue];
          return next;
        });
      bump(1);
      return () => bump(-1);
    }, []);
    const context = React.useMemo(
      () => ({ value, select, panels, registerPanel }),
      [value, select, panels, registerPanel],
    );

    return (
      <NavListTabsContext.Provider value={context}>
        <TabsPrimitive.Root
          ref={ref}
          orientation="vertical"
          value={value}
          onValueChange={select}
          {...props}
        />
      </NavListTabsContext.Provider>
    );
  },
);

export type NavListProps = React.HTMLAttributes<HTMLElement> & {
  /** Names the list for assistive tech, e.g. "Reporting settings sections". */
  "aria-label": string;
};

const LIST_CLASSNAME = "flex flex-col gap-0.5 self-stretch";

const NavList = React.forwardRef<HTMLElement, NavListProps>(function NavList(
  { className, children, ...props },
  ref,
) {
  const inTabs = React.useContext(NavListTabsContext) != null;
  if (inTabs) {
    return (
      <TabsPrimitive.List
        ref={ref as React.Ref<HTMLDivElement>}
        className={cn(LIST_CLASSNAME, className)}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
    );
  }
  return (
    <nav
      ref={ref}
      className={cn("flex flex-col self-stretch", className)}
      {...props}
    >
      <ul className={LIST_CLASSNAME}>{children}</ul>
    </nav>
  );
});

const ROW_CLASSNAME = cn(
  // 32px row, 14px / 500, an 18px leading icon. rounded-lg (10px) in a pane
  // whose inset is larger than its radius, so the nested-radius rule doesn't
  // apply; pass a className where the list sits tight in a rounded frame.
  "group/nav-row flex h-8 w-full min-w-0 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-1.5 text-left",
  "font-sans-v4 text-s-v4 font-medium-v4 transition-colors",
  "text-text-secondary hover:bg-state-hover hover:text-text-primary",
  "data-[selected]:bg-bg-tertiary data-[selected]:text-text-primary data-[selected]:hover:bg-bg-tertiary",
  "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border",
  "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent data-[disabled]:hover:text-text-secondary",
  "[&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4.5",
);

function rowParts(
  icon: React.ReactNode,
  count: number | undefined,
): { before: React.ReactNode; after: React.ReactNode } {
  return {
    before: icon ? (
      <span
        aria-hidden
        className="flex shrink-0 text-icon-secondary group-hover/nav-row:text-icon-primary group-data-[disabled]/nav-row:text-icon-secondary group-data-[selected]/nav-row:text-icon-primary group-data-[state=active]/nav-row:text-icon-primary"
      >
        {icon}
      </span>
    ) : null,
    after: count != null ? <TabStripCount count={count} /> : null,
  };
}

type NavListItemBaseProps = {
  /** A leading 18px icon, rendered at size-4.5 unless it has a size-*. */
  icon?: React.ReactNode;
  /** The count chip after the label (the one Tabs uses). */
  count?: number;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
};

export type NavListItemProps = NavListItemBaseProps &
  Omit<
    React.AnchorHTMLAttributes<HTMLAnchorElement> &
      React.ButtonHTMLAttributes<HTMLButtonElement>,
    keyof NavListItemBaseProps | "value" | "type"
  > & {
    /** Inside NavListTabs: the row's tab value. */
    value?: string;
    /** Outside NavListTabs: the row is the current page / section. */
    current?: boolean;
    /** Outside NavListTabs: render a link. */
    href?: string;
    /** Outside NavListTabs: render the child (e.g. a Next Link) as the row. */
    asChild?: boolean;
  };

type ButtonRest = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "type"
>;
type AnchorRest = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "type">;

const NavListItem = React.forwardRef<HTMLElement, NavListItemProps>(
  function NavListItem(
    {
      value,
      current = false,
      href,
      asChild = false,
      icon,
      count,
      disabled = false,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const tabs = React.useContext(NavListTabsContext);
    const pressed = React.useRef(false);
    const { before, after } = rowParts(icon, count);
    const label = <span className="min-w-0 flex-1 truncate">{children}</span>;

    if (tabs) {
      if (value === undefined) {
        throw new Error("NavListItem inside NavListTabs needs a value");
      }
      return (
        <TabsPrimitive.Trigger
          ref={ref as React.Ref<HTMLButtonElement>}
          value={value}
          disabled={disabled}
          // Radix points aria-controls at the row's panel id even when no
          // panel is mounted (a switcher, or only the selected panel
          // renders); keep it only when the panel is there.
          {...(tabs.panels[value] ? {} : { "aria-controls": undefined })}
          className={cn(
            ROW_CLASSNAME,
            "data-[state=active]:bg-bg-tertiary data-[state=active]:text-text-primary data-[state=active]:hover:bg-bg-tertiary",
            className,
          )}
          {...(props as ButtonRest)}
          onMouseDown={(event) => {
            pressed.current = event.button === 0;
            props.onMouseDown?.(event as React.MouseEvent<HTMLButtonElement>);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              pressed.current = true;
            }
            props.onKeyDown?.(event as React.KeyboardEvent<HTMLButtonElement>);
          }}
          onBlur={(event) => {
            pressed.current = false;
            props.onBlur?.(event as React.FocusEvent<HTMLButtonElement>);
          }}
          onClick={(event) => {
            props.onClick?.(event as React.MouseEvent<HTMLButtonElement>);
            const wasPressed = pressed.current;
            pressed.current = false;
            // Radix selects on mousedown and Enter / Space. A click with
            // neither before it selects here: assistive tech, a scripted
            // click, or an ancestor that stops mousedown in capture (the
            // add-media dialog does, so Radix never sees the press).
            if (wasPressed) return;
            if (!event.defaultPrevented && !disabled && tabs.value !== value) {
              tabs.select(value);
            }
          }}
        >
          {before}
          {label}
          {after}
        </TabsPrimitive.Trigger>
      );
    }

    const stateProps = {
      "data-selected": current ? "" : undefined,
      "data-disabled": disabled ? "" : undefined,
      "aria-current": current ? ("page" as const) : undefined,
    };

    let row: React.ReactNode;
    if (asChild) {
      // The child (e.g. a Next Link) becomes the row: the icon, the
      // truncating label and the count go inside it. A disabled row drops
      // the child's own click and tab stop.
      const child = React.Children.only(children) as React.ReactElement<{
        children?: React.ReactNode;
      }>;
      const { onClick, ...slotProps } = props;
      row = (
        <Slot
          ref={ref}
          className={cn(ROW_CLASSNAME, className)}
          aria-disabled={disabled || undefined}
          {...stateProps}
          {...slotProps}
          onClick={disabled ? undefined : onClick}
        >
          {React.cloneElement(child, {
            children: (
              <>
                {before}
                <span className="min-w-0 flex-1 truncate">
                  {child.props.children}
                </span>
                {after}
              </>
            ),
            ...(disabled && {
              tabIndex: -1,
              onClick: (event: React.MouseEvent<HTMLElement>) =>
                event.preventDefault(),
            }),
          })}
        </Slot>
      );
    } else if (href !== undefined) {
      row = (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          // A disabled link has no href, so it is not focusable or
          // followable; aria-disabled keeps it announced.
          href={disabled ? undefined : href}
          role={disabled ? "link" : undefined}
          aria-disabled={disabled || undefined}
          className={cn(ROW_CLASSNAME, className)}
          {...stateProps}
          {...(props as AnchorRest)}
        >
          {before}
          {label}
          {after}
        </a>
      );
    } else {
      row = (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          disabled={disabled}
          className={cn(ROW_CLASSNAME, className)}
          {...stateProps}
          {...(props as ButtonRest)}
        >
          {before}
          {label}
          {after}
        </button>
      );
    }
    return <li className="flex">{row}</li>;
  },
);

/** A row's panel inside NavListTabs (role=tabpanel). With forceMount the
 * panel stays mounted (keeping its state) and hides while inactive. */
const NavListPanel = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(function NavListPanel({ className, value, forceMount, ...props }, ref) {
  const tabs = React.useContext(NavListTabsContext);
  const registerPanel = tabs?.registerPanel;
  const selected = tabs?.value === value;
  const mounted = forceMount === true || selected;
  React.useLayoutEffect(
    () => (mounted ? registerPanel?.(value) : undefined),
    [registerPanel, value, mounted],
  );
  return (
    <TabsPrimitive.Content
      ref={ref}
      // Not a Tab stop of its own: the panel's first control is.
      tabIndex={-1}
      value={value}
      forceMount={forceMount}
      className={cn(
        "focus-visible:outline-none data-[state=inactive]:hidden",
        className,
      )}
      {...props}
    />
  );
});

export { NavListTabs, NavList, NavListItem, NavListPanel };
